import {computed, effect, inject, Injectable, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {AuthenticationService, UserDataEntity} from '@modules/authentication';
import {CommunicationCenterService} from '@modules/communication-center';
import {TranslateService} from '@ngx-translate/core';
import {FuseConfirmDialogComponent} from 'fuse-core/components/confirm-dialog/confirm-dialog.component';
import {cloneDeep} from 'lodash-es';
import {filter, map, mergeMap} from 'rxjs/operators';
import {AnyLinkType} from 'shared/pretty-easy-navigation/models';
import {PrettyEasyNavigationService} from 'shared/pretty-easy-navigation/pretty-easy-navigation.service';
import {UsagesService} from 'shared/usages.service';
import {brand} from '../../../../../src/app/settings';
import {environment} from '../../../../../src/environments/environment';
import {MenuBuilderMiddleware} from './models/menu-builder.middleware';
import {GuestMenu, LearnerMenu, TrainerMenu} from './models/menus';
import {NavigationContext} from './models/navigation.context';
import {ParentMenu} from "./models/menus/parent.menu";

export const GLOBAL_CORPUS_ID = '11057';
export const APP_BASE_URL = () => `${environment.baseAppUrl(brand)}#/session?token=${localStorage.getItem('http_accessToken')?.slice(1, -1)}`;
export const HOME_APP_URL = () => `${APP_BASE_URL()}&redirectPath=lessons/home`;
export const DICTIONARY_APP_URL = () => `${APP_BASE_URL()}&redirectPath=activities/dictionary`;
export const LANGUAGE_LABORATORY_APP_URL = () => `${APP_BASE_URL()}&redirectPath=activities/language-lab`;

@Injectable({
    providedIn: 'root'
})
export class NavigationService {

    private router = inject(Router);
    private matDialog = inject(MatDialog);
    private usagesService = inject(UsagesService);
    private translateService = inject(TranslateService);
    private authService = inject(AuthenticationService);
    private communicationCenterService = inject(CommunicationCenterService);
    private prettyEasyNavigationService = inject(PrettyEasyNavigationService);

    private user$ = this.communicationCenterService.getRoom('authentication').getSubject<UserDataEntity>('userData').pipe(filter(u => !!u));
    private userId = toSignal(this.user$.pipe(map(user => user?.id || '')));
    private usages = toSignal(this.user$.pipe(mergeMap(() => this.usagesService.getUsages())));
    private communityUsage = computed(() => this.usages()?.entities.find(usage => usage.get('label') === this.usagesService.collectiveUsageLabel));
    private autonomyUsage = computed(() => this.usages()?.entities.find(usage => usage.get('label') === this.usagesService.autonomyUsageLabel));

    private writableMenu = signal<AnyLinkType[]>([]);
    private menuName = signal<string>('guest'); // Default menu

    private readonly availableMenus: { [menuName: string]: (context: NavigationContext) => AnyLinkType[] } = {
        trainer: TrainerMenu,
        learner: LearnerMenu,
        parent: ParentMenu,
        guest: GuestMenu,
    };

    private context = computed<NavigationContext>(() => ({
        globalCorpusId: GLOBAL_CORPUS_ID,
        currentUserCorpusId: this.userId()?.toString() ?? '',
        isGar: this.authService.isGAR(),
        usages: {
            communityId: this.communityUsage()?.id?.toString() ?? '',
            autonomyId: this.autonomyUsage()?.id?.toString() ?? '',
        },
        dictionary: {
            link: DICTIONARY_APP_URL(),
            action: this.onDictionaryNavClick.bind(this)
        },
        languageLaboratory: {
            link: LANGUAGE_LABORATORY_APP_URL(),
            action: this.onLanguageLaboratoryNavClick.bind(this)
        },
        learnerApp: {
            link: HOME_APP_URL(),
            action: this.onLearnerAppNavClick.bind(this)
        },
    }));
    private menuBuilderMiddlewares = signal<{ [key: string]: MenuBuilderMiddleware }>({});


    public menu = this.writableMenu.asReadonly();

    constructor() {
        effect(() => {
            const menuName = this.menuName();
            if (this.availableMenus[menuName]) {
                this.writableMenu.set(this.buildUpMenu(menuName));
            } else {
                console.warn(`Menu with name "${menuName}" does not exist.`);
                this.writableMenu.set([]);
            }
            this.prettyEasyNavigationService.setMenu(this.writableMenu());
        }, {allowSignalWrites: true});
    }

    private buildUpMenu(menuName: string) {
        const menuGenerator = this.availableMenus[menuName];
        const context = this.context();
        const builtMenu = menuGenerator(context);
        const middlewares = this.menuBuilderMiddlewares();

        return Object.values(middlewares).reduce((menu, middleware) => {
            return middleware(cloneDeep(menu));
        }, builtMenu);
    }

    public setMenu(menuName: string) {
        this.menuName.set(menuName);
    }

    public addMenuBuilderMiddleware(middlewareId: string, middleware: MenuBuilderMiddleware): void {
        const currentMiddlewares = this.menuBuilderMiddlewares();
        if (currentMiddlewares[middlewareId]) {
            console.warn(`Middleware with id "${middlewareId}" already exists. It will be replaced.`);
        }
        currentMiddlewares[middlewareId] = middleware;
        this.menuBuilderMiddlewares.set(cloneDeep(currentMiddlewares)); // cloneDeep to trigger change detection
    }


    private askConsent(url: string, suffix: string): void {
        const dialogConfig = new MatDialogConfig();
        const role = this.authService.accessLevel;
        dialogConfig.data = {
            bodyDialog: 'navigation.redirect_' + role + '_to_app' + suffix,
            labelTrueDialog: 'generic.confirm',
            labelFalseDialog: 'generic.cancel'
        };
        this.openFuseConfirmDialog(dialogConfig)
            .afterClosed()
            .subscribe(result => {
                if (result === true) {
                    // store current url to go on the same place when come back from app
                    localStorage.setItem('origin', this.router.url);
                    window.open(url, '_self'); // Redirige l'utilisateur si le résultat est true
                }
            });
    }

    private openFuseConfirmDialog(dialogConfig: MatDialogConfig): MatDialogRef<FuseConfirmDialogComponent> {
        for (const term in dialogConfig.data) {
            this.translateService.get(dialogConfig.data[term]).subscribe((translation: string) => dialogConfig.data[term] = translation);
        }

        return this.matDialog.open(FuseConfirmDialogComponent, dialogConfig);
    }

    private onDictionaryNavClick(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.askConsent(DICTIONARY_APP_URL(), '_dictionary');
    }

    private onLanguageLaboratoryNavClick(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.askConsent(LANGUAGE_LABORATORY_APP_URL(), '_language_laboratory');
    }

    private onLearnerAppNavClick(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.askConsent(HOME_APP_URL(), '_home');
    }
}
